'use client'

import { Fragment } from 'react'

import AppIcon from '@/components/content/app/AppIcon'
import ScrollView from '@/components/ui/ScrollView'
import Breadcrumb from '@/components/content/sidebar/Breadcrumb'
import Menu from '@/components/ui/Menu'
import Link from '@/components/ui/Link'
import { PREFETCH } from '@/lib/prefetch'
import { createLogger } from '@/lib/logger'
import { getBrandColors, isPopulatedObject } from '@/lib/utils'
import type { MenuItem } from '@/helpers/menus'
import type { App, Media } from '@/payload-types'

const logger = createLogger('CompareSidebar')

type DealSidebarProps = {
  apps: App[]
  menu: MenuItem[]
}

export default function CompareSidebar({ apps, menu }: DealSidebarProps) {
  return (
    <div className="flex w-full flex-row gap-3.5 md:flex-col md:py-4 md:pr-4">
      <div className="flex min-w-0 flex-1 items-center gap-4">
        <ScrollView
          className="flex gap-2.5 md:pl-4"
          radius="flex-0 max-w-fit md:flex-1 md:max-w-full"
          trigger={apps}
        >
          {apps.map((app) => {
            const appIcon = app.icon as Media
            const appGlyph = app.glyph as Media

            if (!isPopulatedObject<Media>(appIcon) && !isPopulatedObject<Media>(appGlyph)) {
              logger.error('App icon or glyph is not populated in CompareSidebar')
              return null
            }
            const { backgroundColor } = getBrandColors(app)
            return (
              <div key={app.id} className="h-8 w-8 shrink-0 md:h-14 md:w-14">
                <AppIcon
                  icon={appIcon}
                  glyph={appGlyph}
                  name={app.name}
                  slug={app.slug}
                  background={backgroundColor}
                />
              </div>
            )
          })}
        </ScrollView>
        <Breadcrumb placeholder="Comparison" />
        <Menu items={menu} subAlign="down" />
      </div>

      <div className="hidden flex-col gap-0.5 px-4 md:flex">
        <span className="font-semibold text-gray-950">Compare Apps</span>
        <div className="flex gap-1 truncate text-13 leading-none">
          {apps.map((app, index) => (
            <Fragment key={app.id}>
              <Link href={`/apps/${app.slug}`} prefetch={PREFETCH} className="text-gray-600">
                {app.name}
              </Link>
              {index < apps.length - 1 && <span className="text-gray-400">vs</span>}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}
